import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { unitApi } from '../services/api';
import type { Unit } from '../types/domain';

export type { Unit } from '../types/domain';

interface UnitState {
  units: Unit[];
  selectedUnitId: string | null; // null = all units (Brigade HQ view), or specific unit ID
  selectedUnitIds: string[]; // For Brigade HQ: can filter by multiple units
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchUnits: () => Promise<void>;
  setSelectedUnit: (unitId: string | null) => void;
  setSelectedUnitId: (unitId: string | null) => void;
  setSelectedUnitIds: (unitIds: string[]) => void; // For multi-select filtering
  toggleUnitFilter: (unitId: string) => void; // Toggle a unit in multi-select
  getSelectedUnit: () => Unit | null;
      isHQ: (userUnitId?: string | null) => boolean;
  getEffectiveUnitIds: (userUnitId?: string | null) => string[] | null; // Returns array of unit IDs to filter, or null for all
}

export const useUnitStore = create<UnitState>()(
  persist(
    (set, get) => ({
      units: [],
      selectedUnitId: null,
      selectedUnitIds: [], // For Brigade HQ multi-select filtering
      isLoading: false,
      error: null,

      fetchUnits: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await unitApi.getAll();
          set({
            units: response.data.units || [],
            isLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Failed to fetch units',
            isLoading: false,
          });
        }
      },

      setSelectedUnit: (unitId: string | null) => {
        set({ 
          selectedUnitId: unitId,
          selectedUnitIds: unitId ? [unitId] : [] // Reset multi-select when single select
        });
      },
      setSelectedUnitId: (unitId: string | null) => {
        set({ 
          selectedUnitId: unitId,
          selectedUnitIds: unitId ? [unitId] : [] // Reset multi-select when single select
        });
      },

      setSelectedUnitIds: (unitIds: string[]) => {
        set({ 
          selectedUnitIds: unitIds,
          selectedUnitId: unitIds.length === 1 ? unitIds[0] : null // Set single if only one selected
        });
      },

      toggleUnitFilter: (unitId: string) => {
        const { selectedUnitIds } = get();
        const newIds = selectedUnitIds.includes(unitId)
          ? selectedUnitIds.filter(id => id !== unitId)
          : [...selectedUnitIds, unitId];
        get().setSelectedUnitIds(newIds);
      },

      getSelectedUnit: () => {
        const { units, selectedUnitId } = get();
        if (!selectedUnitId) return null;
        return units.find(b => b.id === selectedUnitId) || null;
      },

      isHQ: (userUnitId?: string | null) => {
        // A token with no unit is an HQ principal.
        if (userUnitId === null || userUnitId === undefined) return true;
        // Otherwise HQ is driven purely by the seeded UnitRole — never by unit code.
        const u = get().units.find(x => x.id === userUnitId);
        return u?.role === 'HQ';
      },

      getEffectiveUnitIds: (userUnitId?: string | null) => {
        const { selectedUnitId, selectedUnitIds, units } = get();
        const knowUnits = units.length > 0;

        // A player-unit user is locked to their own unit and cannot see others.
        if (userUnitId) {
          // Exception: an HQ-role user sees all units.
          const userUnit = units.find((x) => x.id === userUnitId);
          if (userUnit?.role === 'HQ') return null;
          return [userUnitId];
        }

        // For HQ users (userUnitId === null): a selection only scopes the view when it
        // points at a REAL, loaded unit. A stale/invalid persisted selection (an id no
        // longer in `units`, e.g. left over from another deployment) must fall through to
        // "all units" (null) instead of silently hiding every platoon — the map page has
        // no unit selector to recover from that.
        if (selectedUnitId) {
          const sel = units.find((x) => x.id === selectedUnitId);
          if (sel) {
            // Selecting the HQ unit means "ALL units".
            return sel.role === 'HQ' ? null : [selectedUnitId];
          }
          // Not a loaded unit: stale once units are known → ALL units. Before units load
          // we can't validate, so keep the id (avoids a flash of "all" for a valid pick).
          if (knowUnits) return null;
          return [selectedUnitId];
        }

        // HQ multi-select filters: keep only ids that are real loaded units.
        if (selectedUnitIds.length > 0) {
          const valid = knowUnits
            ? selectedUnitIds.filter((id) => units.some((x) => x.id === id))
            : selectedUnitIds;
          if (valid.length === 0) return null; // all stale/invalid → ALL units
          // If the HQ unit is included in filters, treat as ALL units.
          const hasHQ = valid.some((id) => units.find((x) => x.id === id)?.role === 'HQ');
          if (hasHQ) return null;
          return valid;
        }

        // No selection → all units (HQ default).
        return null;
      },
    }),
    {
      name: 'opmonitor-unit',
      partialize: (state) => ({
        selectedUnitId: state.selectedUnitId,
        selectedUnitIds: state.selectedUnitIds,
      }),
    }
  )
);
